import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { articlesAPI } from '../services/api';
import { editorialConfig, getBalancedArticles } from '../utils/editorialConfig';
import { formatPublishTime, formatAuthorByline } from '../utils/dateUtils';

const ArticleContext = createContext();

const initialState = {
  articles: [],
  featuredArticles: [],
  currentArticle: null,
  loading: false,
  error: null,
  categories: ['Film', 'TV', 'Awards', 'Festivals', 'Industry', 'Reviews'],
};

function articleReducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload, error: null };
    case 'SET_ARTICLES':
      return { ...state, articles: action.payload, loading: false };
    case 'SET_FEATURED_ARTICLES':
      return { ...state, featuredArticles: action.payload };
    case 'SET_CURRENT_ARTICLE':
      return { ...state, currentArticle: action.payload, loading: false };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    default:
      return state;
  }
}

export function ArticleProvider({ children }) {
  const [state, dispatch] = useReducer(articleReducer, initialState);

  const fetchArticles = async (page = 1, limit = 100) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      
      // Fetch from AWS API (with WordPress fallback)
      const response = await articlesAPI.getArticles(page, limit * 2);
      
      let latestArticles;
      
      // Handle response format
      const posts = response.posts || response || [];
      
      // Check if data is from AWS (already formatted) or WordPress (needs formatting)
      if (Array.isArray(posts) && posts[0]?.authorByline) {
        // AWS data - already formatted
        latestArticles = posts;
      } else {
        // WordPress data - needs formatting
        latestArticles = posts.map(post => {
          const categories = post.categories || [];
          let categoryName = 'News';
          
          // Map WordPress category IDs to names
          Object.entries(editorialConfig.categoryMapping).forEach(([id, name]) => {
            if (categories.includes(parseInt(id))) {
              categoryName = name;
            }
          });
          
          return {
            id: post.id,
            title: post.title.rendered,
            subtitle: post.excerpt.rendered.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').substring(0, 120) + '...',
            content: post.content.rendered,
            image: post.jetpack_featured_media_url || 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=800&h=400&fit=crop',
            author: post._embedded?.author?.[0]?.name || 'Editorial Team',
            authorByline: formatAuthorByline(post._embedded?.author?.[0]?.name || 'Editorial Team'),
            publishTime: formatPublishTime(post.date),
            fullDate: post.date,
            category: categoryName
          };
        });
      }
      
      // Filter out featured articles if configured
      let filteredArticles = latestArticles;
      if (editorialConfig.latestArticles.excludeFeatured && state.featuredArticles.length > 0) {
        const featuredIds = new Set(state.featuredArticles.map(a => a.id));
        filteredArticles = latestArticles.filter(a => !featuredIds.has(a.id));
      }
      
      // Don't limit articles - use all available
      const finalArticles = filteredArticles;
      
      dispatch({ type: 'SET_ARTICLES', payload: finalArticles });
    } catch (error) {
      console.error('Error fetching articles:', error);
      const errorMessage = error.message.includes('fetch') 
        ? 'Network connection error. Please check your internet connection and try again.'
        : error.message;
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
    }
  };

  const fetchFeaturedArticles = async () => {
    try {
      const response = await articlesAPI.getFeaturedArticles();
      
      let allArticles;
      
      // Handle response format
      const posts = response.posts || response || [];
      
      // Check if data is from AWS (already formatted) or WordPress (needs formatting)
      if (Array.isArray(posts) && posts[0]?.authorByline) {
        // AWS data - already formatted
        allArticles = posts.map(article => ({
          ...article,
          wpCategories: [],
          isEditorialPick: false
        }));
      } else {
        // WordPress data - needs formatting
        allArticles = posts.map(post => {
          // Get WordPress categories
          const categories = post.categories || [];
          let categoryName = 'News';
          
          // Map WordPress category IDs to names using editorial config
          Object.entries(editorialConfig.categoryMapping).forEach(([id, name]) => {
            if (categories.includes(parseInt(id))) {
              categoryName = name;
            }
          });
          
          return {
            id: post.id,
            title: post.title.rendered,
            subtitle: post.excerpt.rendered.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').substring(0, 100) + '...',
            content: post.content.rendered,
            image: post.jetpack_featured_media_url || 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=800&h=400&fit=crop',
            author: post._embedded?.author?.[0]?.name || 'Editorial Team',
            authorByline: formatAuthorByline(post._embedded?.author?.[0]?.name || 'Editorial Team'),
            publishTime: formatPublishTime(post.date),
            fullDate: post.date,
            category: categoryName,
            wpCategories: categories,
            isEditorialPick: false
          };
        });
      }
      
      // Apply editorial curation
      let featuredArticles = [];
      
      // 1. Check for manually selected hero story
      if (editorialConfig.heroStory.postId) {
        const heroArticle = allArticles.find(a => a.id === editorialConfig.heroStory.postId);
        if (heroArticle) {
          heroArticle.isEditorialPick = true;
          featuredArticles.push(heroArticle);
        }
      }
      
      // 2. Get balanced category selection for remaining slots
      const remainingArticles = allArticles.filter(a => !featuredArticles.includes(a));
      const balancedArticles = getBalancedArticles(
        remainingArticles, 
        editorialConfig.featuredStories.categoryBalance
      );
      
      // 3. Combine hero + balanced articles
      featuredArticles = [...featuredArticles, ...balancedArticles];
      
      // 4. Ensure we have exactly the right number
      const totalNeeded = editorialConfig.featuredStories.totalCount + (featuredArticles.length > 0 ? 1 : 0);
      featuredArticles = featuredArticles.slice(0, totalNeeded);
      
      dispatch({ type: 'SET_FEATURED_ARTICLES', payload: featuredArticles });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  const fetchArticle = useCallback(async (id) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const article = await articlesAPI.getArticle(id);
      if (article) {
        dispatch({ type: 'SET_CURRENT_ARTICLE', payload: article });
      } else {
        dispatch({ type: 'SET_ERROR', payload: 'Article not found' });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Article not found' });
    }
  }, []);

  useEffect(() => {
    fetchFeaturedArticles();
    fetchArticles();
  }, []);

  const value = {
    ...state,
    fetchArticles,
    fetchFeaturedArticles,
    fetchArticle,
  };

  return (
    <ArticleContext.Provider value={value}>
      {children}
    </ArticleContext.Provider>
  );
}

export const useArticles = () => {
  const context = useContext(ArticleContext);
  if (!context) {
    throw new Error('useArticles must be used within ArticleProvider');
  }
  return context;
};